/* eslint-disable react/no-multi-comp */
/**
 * SP-8387 — AND/OR boolean logic for automated parameters.
 *
 * Each automated-parameter row is one condition; consecutive rows are joined by a
 * per-row logical operator (AND/OR). This is the FLAT model the sports-api backend
 * persists (RestFilter.logicalOperator on each condition, evaluated AND-before-OR).
 *
 * Grouping/brackets are intentionally out of scope for now — when the backend grows
 * a nested shape, the recursive expression below already renders bracketed groups,
 * so only the editor needs to gain a "group" affordance. Until then the preview
 * shows the implicit AND-before-OR precedence so users can read the resulting logic.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Segmented, Select, Tooltip, Typography } from '@app/ui';
import { PartitionOutlined, ReadOutlined } from '@ant-design/icons';

export type LogicalOperator = 'AND' | 'OR';

// Relo brand accents (match the SP-8141 prototype, not the Ant defaults).
const BRAND_BLUE = '#009FDF';
const BRAND_ORANGE = '#FF7500';
const accentOf = (op: LogicalOperator) =>
    op === 'OR' ? BRAND_ORANGE : BRAND_BLUE;

// Relo brand yellow — used to make the picked AND/OR option unmistakable.
const BRAND_YELLOW = '#ffd100';

// Stable theme object: a fresh inline theme on every render makes ConfigProvider
// regenerate its styles mid-toggle, which interrupts the Segmented thumb's slide
// transition inconsistently (the animation looked different AND→OR vs OR→AND).
const CONNECTOR_THEME = {
    components: {
        Segmented: {
            // Picked option = branded yellow with dark text so the selection reads
            // at a glance (default gray was too subtle).
            itemSelectedBg: BRAND_YELLOW,
            itemSelectedColor: '#1a1a1a',
        },
    },
};

const CONNECTOR_OPTIONS = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' },
];

// Lowercase, prose-style labels for the compact borderless operator select (matches
// the HubSpot-style "or ▾" affordance the builder is modeled on).
const OPERATOR_SELECT_OPTIONS = [
    { label: 'and', value: 'AND' },
    { label: 'or', value: 'OR' },
];

/* ---------- per-row connector ---------- */

type LogicalConnectorProps = {
    value: LogicalOperator;
    onChange: (v: LogicalOperator) => void;
    disabled?: boolean;
};

/** The AND/OR control rendered in the gap between two consecutive condition rows. */
export const LogicalConnector: React.FC<LogicalConnectorProps> = ({
    value,
    onChange,
    disabled,
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            margin: '6px 0 6px 14px',
        }}
        data-test="logical-connector"
    >
        <span style={{ width: 1, height: 18, background: '#d4dae3' }} />
        <ConfigProvider theme={CONNECTOR_THEME}>
            <Segmented
                size="small"
                value={value}
                disabled={disabled}
                onChange={v => onChange(v as LogicalOperator)}
                options={CONNECTOR_OPTIONS}
            />
        </ConfigProvider>
    </div>
);

/** AND/OR operator as a compact, borderless Select — the small left-rail "or ▾"
 *  affordance (modeled on HubSpot's filter builder) that sits to the LEFT of each
 *  condition (2nd onward) so operators read as a vertical column the user can change in
 *  place. Wrapped in a data-test div because antd Select does not reliably forward
 *  data-test to the DOM. */
export const OperatorToggle: React.FC<LogicalConnectorProps> = ({
    value,
    onChange,
    disabled,
}) => (
    <div data-test="operator-toggle">
        <Select
            size="small"
            variant="borderless"
            value={value}
            disabled={disabled}
            onChange={v => onChange(v as LogicalOperator)}
            options={OPERATOR_SELECT_OPTIONS}
            popupMatchSelectWidth={false}
            style={{ width: '100%', color: '#5b6675' }}
        />
    </div>
);

/* ---------- animated-height wrapper ---------- */

/**
 * Animates its own height to follow the content, so a growing/shrinking child
 * (the resulting-logic preview living ABOVE the builder) nudges the layout in
 * one smooth glide instead of a per-keystroke jump. With `maxContentHeight`
 * the content scrolls internally past the cap, so the wrapper — and everything
 * below it — stops moving entirely once the preview is a few lines tall.
 */
export const AnimatedHeight: React.FC<{
    children: React.ReactNode;
    maxContentHeight?: number;
}> = ({ children, maxContentHeight }) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        const el = innerRef.current;
        // jsdom (tests) has no ResizeObserver — fall back to natural height.
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    return (
        <div
            style={{
                height: height ?? 'auto',
                overflow: 'hidden',
                transition: reduceMotion ? undefined : 'height 180ms ease',
            }}
        >
            <div
                ref={innerRef}
                style={
                    maxContentHeight
                        ? { maxHeight: maxContentHeight, overflowY: 'auto' }
                        : undefined
                }
            >
                {children}
            </div>
        </div>
    );
};

/* ---------- resulting-logic preview ---------- */

/** A flat condition: a label (the parameter name) plus a value summary, joined to
 *  the previous condition by `operator` (undefined on the first condition). The
 *  summary is split into a comparator `verb` ("is any of", "contains" …) and the
 *  selected `values` so the preview can style them differently. `detail` is the
 *  combined "verb values" string kept as a fallback for plain-text consumers. */
export type PreviewCondition = {
    label: string;
    verb?: string;
    values?: string;
    detail?: string;
    operator?: LogicalOperator;
};

/** One condition rendered as a chip: bold field label + muted comparator verb +
 *  the selected values. Values are bold (not colored) so they stand out as the
 *  picked items without clashing with the AND/OR accent colors. */
const Chip: React.FC<{ label: string; verb?: string; values?: string }> = ({
    label,
    verb,
    values,
}) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 6,
            padding: '3px 10px',
            border: '1px solid #e4e9f0',
            borderRadius: 14,
            background: '#fff',
            fontSize: 13,
            maxWidth: '100%',
        }}
    >
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
        {verb && <span style={{ color: '#5b6675' }}>{verb}</span>}
        {values && (
            <span
                style={{
                    fontWeight: 700,
                    color: '#1a1a1a',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {values}
            </span>
        )}
    </span>
);

/** Solid filled AND/OR pill — used between conditions in BOTH preview modes so the
 *  operator reads identically. In Sentence mode it sits inline in the flowing line
 *  (`inline` adds horizontal breathing room); in Structured mode it stacks between
 *  rows. */
const OperatorPill: React.FC<{ op: LogicalOperator; inline?: boolean }> = ({
    op,
    inline,
}) => (
    <span
        style={{
            display: 'inline-block',
            background: accentOf(op),
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            padding: '1px 8px',
            borderRadius: 4,
            ...(inline ? { margin: '0 6px' } : null),
        }}
    >
        {op}
    </span>
);

type PreviewMode = 'structured' | 'sentence';

/** A single-level group (bracket) in the preview: its conditions each keep their own
 *  operator; the group as a whole joins the previous top-level item by `operator`. */
export type PreviewGroup = {
    kind: 'group';
    operator?: LogicalOperator;
    conditions: PreviewCondition[];
};

/** A top-level preview item: a bare condition or a group of conditions. */
export type PreviewNode =
    | ({ kind: 'condition' } & PreviewCondition)
    | PreviewGroup;

/** Plain parenthesis mark used to bracket a group in BOTH modes (no boxed band —
 *  brackets alone convey the grouping). */
const Paren: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span style={{ color: '#5b6675', fontWeight: 700 }}>{children}</span>
);

/** Structured mode: a group as a soft-grey filled block containing its chips stacked
 *  with AND/OR pills between them. The negative left margin cancels the fill's own left
 *  padding so the group's CONTENT stays on the same column as the top-level operators and
 *  ungrouped rows — the grey band just bleeds ~12px left behind the grouped rows. */
const StructuredGroup: React.FC<{ group: PreviewGroup }> = ({ group }) => (
    <div
        style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 6,
            borderRadius: 10,
            background: '#eef2f6',
            padding: '10px 12px',
            marginLeft: -12,
        }}
        data-test="preview-group"
    >
        {group.conditions.map((c, j) => (
            <React.Fragment key={j}>
                {j > 0 && <OperatorPill op={c.operator ?? 'AND'} />}
                <Chip label={c.label} verb={c.verb} values={c.values} />
            </React.Fragment>
        ))}
    </div>
);

/* ---------- Editor-mirror rendering (confirm-modal review) ---------- */

/** A condition rendered as a full-width row card — mirrors a builder row so the review
 *  reads like the editor the user just used. */
const EditorRow: React.FC<{ condition: PreviewCondition }> = ({
    condition: c,
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            background: '#fff',
            border: '1px solid #e4e9f0',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 13,
        }}
    >
        <span style={{ fontWeight: 650, color: '#17222e' }}>{c.label}</span>
        {c.verb && <span style={{ color: '#5b6675' }}>{c.verb}</span>}
        {c.values && (
            <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{c.values}</span>
        )}
    </div>
);

/** AND/OR connector between rows (indented) or between top-level items — a short rail
 *  plus the operator pill, matching the builder's connector affordance. */
const EditorConnector: React.FC<{
    op: LogicalOperator;
    topLevel?: boolean;
}> = ({ op, topLevel }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: topLevel ? '6px 0' : '6px 0 6px 10px',
        }}
    >
        <span style={{ width: 2, height: 16, background: '#d4dae3' }} />
        <OperatorPill op={op} />
    </div>
);

/* ---------- Sentence (plain-English) rendering ---------- */

/** Highlighted field name in the plain-English sentence. */
const FieldHighlight: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <span
        style={{
            // App Tag bg (#E6F4FF) + brand blue text, coherent with existing UI.
            background: '#E6F4FF',
            color: '#0480B2',
            borderRadius: 4,
            padding: '0 5px',
            fontWeight: 600,
        }}
    >
        {children}
    </span>
);

/** AND/OR rendered as a lowercase prose word ("and" / "or"), colour-coded. */
const OperatorWord: React.FC<{ op: LogicalOperator }> = ({ op }) => (
    <span style={{ color: accentOf(op), fontWeight: 700, margin: '0 5px' }}>
        {op === 'OR' ? 'or' : 'and'}
    </span>
);

/** One condition as a prose phrase: highlighted field + comparator words + values. */
const SentenceCondition: React.FC<{ condition: PreviewCondition }> = ({
    condition: c,
}) => (
    <span>
        <FieldHighlight>{c.label}</FieldHighlight>
        {c.verb && <span style={{ color: '#5b6675' }}> {c.verb}</span>}
        {c.values && <span style={{ fontWeight: 600 }}> {c.values}</span>}
    </span>
);

/** Sentence mode: a group as a parenthesised sub-phrase, members joined by word ops. */
const SentenceGroup: React.FC<{ group: PreviewGroup }> = ({ group }) => (
    <span data-test="preview-group">
        <Paren>(</Paren>{' '}
        {group.conditions.map((c, j) => (
            <React.Fragment key={j}>
                {j > 0 && <OperatorWord op={c.operator ?? 'AND'} />}
                <SentenceCondition condition={c} />
            </React.Fragment>
        ))}{' '}
        <Paren>)</Paren>
    </span>
);

type ResultingLogicPreviewProps = {
    conditions: PreviewCondition[];
    /** SP-8647: when provided, renders the grouped structure (brackets) instead of the
     *  flat conditions list. Each item is a bare condition or a group. */
    items?: PreviewNode[];
    /** Localized "Resulting logic" label + "no conditions" empty text. */
    title: string;
    emptyText: string;
    /** Hide the section header (label + mode toggle) and divider — used in the confirm
     *  dialog, which supplies its own heading. Renders just the logic, tightly. */
    hideChrome?: boolean;
    /** When hideChrome, this text sits on the left of the toggle row (so the description
     *  and the Structured/Sentence toggle share one line instead of leaving dead space). */
    heading?: string;
    /** Animate the logic area's height (AnimatedHeight) and cap it at this many px,
     *  scrolling internally past the cap. Used by the in-drawer preview, which sits
     *  ABOVE the builder — growth must not jolt the rows being edited. */
    maxContentHeight?: number;
};

/**
 * Read-only summary of how the conditions combine, in document order with each row's
 * AND/OR and any group shown in brackets. Two deliberately distinct display modes,
 * toggled by the icon control and defaulting to Sentence:
 *  - Structured: conditions as chips, AND/OR as colour-coded pills, groups bracketed.
 *  - Sentence: a flowing plain-English line — highlighted fields, "and"/"or" as words,
 *    groups in parentheses.
 */
export const ResultingLogicPreview: React.FC<ResultingLogicPreviewProps> = ({
    conditions,
    items,
    title,
    emptyText,
    hideChrome = false,
    heading,
    maxContentHeight,
}) => {
    const { t } = useTranslation('tools');
    const [mode, setMode] = useState<PreviewMode>('sentence');

    const structuredLabel = t(
        'tools:automatedParams.messages.previewStructured',
        { defaultValue: 'Structured' },
    );
    const sentenceLabel = t('tools:automatedParams.messages.previewSentence', {
        defaultValue: 'Sentence',
    });
    const modeLabel = mode === 'structured' ? structuredLabel : sentenceLabel;

    // SP-8647: render the grouped node list when supplied; otherwise fall back to the
    // flat conditions list (used by the pre-submit summary and legacy callers).
    const nodes: PreviewNode[] =
        items ??
        conditions.map(c => ({ kind: 'condition' as const, ...c }));
    const nodeOp = (i: number): LogicalOperator => nodes[i].operator ?? 'AND';

    const renderStructured = (node: PreviewNode) =>
        node.kind === 'group' ? (
            <StructuredGroup group={node} />
        ) : (
            <Chip label={node.label} verb={node.verb} values={node.values} />
        );

    const renderSentence = (node: PreviewNode) =>
        node.kind === 'group' ? (
            <SentenceGroup group={node} />
        ) : (
            <SentenceCondition condition={node} />
        );

    // Editor-mirror review (confirm modal): labelled group boxes + condition rows +
    // connectors, so the review looks like the builder it's confirming.
    const groupLabel = t('tools:automatedParams.headers.group', {
        defaultValue: 'Group',
    });
    const renderEditorReview = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {nodes.map((node, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <EditorConnector op={nodeOp(i)} topLevel />}
                    {node.kind === 'group' ? (
                        <div
                            style={{
                                border: '1px solid #BFBFBF',
                                borderRadius: 8,
                                background: '#fbfcfe',
                                padding: '8px 10px 10px',
                            }}
                            data-test="preview-group"
                        >
                            <span
                                style={{
                                    display: 'block',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '.05em',
                                    textTransform: 'uppercase',
                                    color: '#8a94a2',
                                    marginBottom: 6,
                                }}
                            >
                                {groupLabel}
                            </span>
                            {node.conditions.map((c, j) => (
                                <React.Fragment key={j}>
                                    {j > 0 && (
                                        <EditorConnector
                                            op={c.operator ?? 'AND'}
                                        />
                                    )}
                                    <EditorRow condition={c} />
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <EditorRow condition={node} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    // The logic area proper — the only part whose height changes as the user edits.
    const renderLogic = () =>
        !nodes.length ? (
            <Typography.Text type="secondary">{emptyText}</Typography.Text>
        ) : mode === 'structured' && hideChrome ? (
            // Confirm modal: editor-mirror review (labelled group boxes + rows).
            renderEditorReview()
        ) : mode === 'structured' ? (
            // Drawer: compact chips + colour-coded AND/OR pills, one item per row, all
            // on a single content column (paddingLeft). Grouped rows sit on a grey band
            // that bleeds ~12px left of that column (StructuredGroup's negative margin)
            // so the top-level operators align with the conditions instead of jutting.
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 6,
                    paddingLeft: 12,
                }}
            >
                {nodes.map((node, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <OperatorPill op={nodeOp(i)} />}
                        {renderStructured(node)}
                    </React.Fragment>
                ))}
            </div>
        ) : (
            // Sentence: a flowing plain-English line — highlighted fields,
            // operators as words, groups in parentheses.
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                {nodes.map((node, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <OperatorWord op={nodeOp(i)} />}
                        {renderSentence(node)}
                    </React.Fragment>
                ))}
            </div>
        );

    return (
        <div data-test="resulting-logic-preview">
            {/* Header: title + mode toggle. In the confirm dialog (hideChrome) the label
                is dropped — the modal supplies its own heading — but the toggle stays so
                the user can still switch Structured/Sentence there. In the drawer the
                header always renders (even with zero conditions) so the sticky summary
                bar keeps a stable, predictable shape from the first interaction. */}
            {(!hideChrome || nodes.length > 0) && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent:
                            !hideChrome || heading
                                ? 'space-between'
                                : 'flex-end',
                        flexWrap: 'wrap',
                        gap: 12,
                        marginBottom: hideChrome ? 12 : 8,
                    }}
                >
                    {!hideChrome ? (
                        // Match the drawer's own field labels (-filter-title); the mode
                        // reads as muted text set apart by spacing (no glyph, no badge).
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'baseline',
                                gap: 10,
                            }}
                        >
                            <Typography.Text className="-filter-title">
                                {title}
                            </Typography.Text>
                            <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                            >
                                {modeLabel}
                            </Typography.Text>
                        </span>
                    ) : (
                        // Confirm dialog: the description shares this row with the toggle.
                        heading && (
                            <Typography.Text style={{ fontSize: 14 }}>
                                {heading}
                            </Typography.Text>
                        )
                    )}
                    <Segmented
                        size="small"
                        value={mode}
                        onChange={v => setMode(v as PreviewMode)}
                        options={[
                            {
                                value: 'sentence',
                                label: (
                                    <Tooltip title={sentenceLabel}>
                                        <ReadOutlined />
                                    </Tooltip>
                                ),
                            },
                            {
                                value: 'structured',
                                label: (
                                    <Tooltip title={structuredLabel}>
                                        <PartitionOutlined />
                                    </Tooltip>
                                ),
                            },
                        ]}
                    />
                </div>
            )}

            {/* Section divider (app hairline) instead of a card frame */}
            {!hideChrome && (
                <div
                    style={{
                        height: 1,
                        background: '#e8e8e8',
                        marginBottom: 12,
                    }}
                />
            )}

            {maxContentHeight ? (
                <AnimatedHeight maxContentHeight={maxContentHeight}>
                    {renderLogic()}
                </AnimatedHeight>
            ) : (
                renderLogic()
            )}
        </div>
    );
};
